import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { CompactBand, BandButton, BandTab } from '@/components/CompactBand';
import {
  FilterBar,
  NumberedPager,
  ListEmpty,
  useListUrlState,
  rangeForPreset,
  windowLabel,
  inRange,
  applySearch,
  sortBy,
  paginate,
  usd,
  type HistoryDatePreset,
  type DateRange,
} from '@/components/lists';
import {
  HISTORY_CLAIMS,
  getHistoryClaims,
  CLAIM_STATUSES,
  CLAIM_STATUS_LABEL,
  type HistoryClaim,
} from '../historyMock';
import { usePaidToPatientVersion } from '@/features/networks/paidToPatient';
import { CLAIMS_TODAY, CLAIMS_TODAY_ISO } from '../today';
import { addDays, isoOf } from '../claimBuilder';
import '../claims.css';

const PAGE_SIZE = 50;

/** Every payer that appears in the claim set, A to Z. */
const PAYER_OPTIONS: string[] = [...new Set(HISTORY_CLAIMS.map((c) => c.payer))].sort();

/** The visit-date window, measured from the claims "today" (Oct 18, 2026) so a
 *  claim from this weekend is inside "Last 90 days" and "This year". */
function windowFor(preset: HistoryDatePreset): DateRange {
  const range = rangeForPreset(preset);
  if (preset === 'last-90') return { from: isoOf(addDays(CLAIMS_TODAY, -90)), to: CLAIMS_TODAY_ISO };
  if (preset === 'this-year') return { ...range, to: CLAIMS_TODAY_ISO };
  return range;
}

/** The strings a row is searched over: patient, claim number, payer claim
 *  number, payer and amount (rcm-25 §4). */
function searchText(c: HistoryClaim): string {
  const amounts = [String(c.billed), c.billed.toFixed(2), usd(c.billed)];
  if (c.paid != null) amounts.push(String(c.paid), c.paid.toFixed(2), usd(c.paid));
  return `${c.patient} ${c.id} ${c.payerClaimNo} ${c.payer} ${amounts.join(' ')}`;
}

/** Download the filtered rows as a CSV (client-side, logged — no backend). */
function exportCsv(rows: HistoryClaim[]): void {
  const head = ['Claim', 'Patient', 'Payer', 'Codes', 'Billed', 'Paid', 'Where it is', 'Visit', 'Last change'];
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [head.join(',')];
  for (const c of rows) {
    lines.push(
      [c.id, c.patient, c.payer, c.codes.join(' '), usd(c.billed), c.paid == null ? '' : usd(c.paid), c.where, c.visitFull, c.lastChangeLabel]
        .map((v) => esc(v))
        .join(','),
    );
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'claims.csv';
  a.click();
  URL.revokeObjectURL(url);
  // Every export is logged (D152). Mock: a console line stands in for the log.
  console.info('export logged', { rows: rows.length });
}

/**
 * /claims/all — "Every claim" (rcm-25, 00-ds-09 §3/§9, D148/D149). One place to
 * find any claim in any state, from waiting-for-review to deposited. A history
 * table over the big mock fixture: search, a visit-date preset, status and
 * payer filters, newest-visit sort with a "last change" toggle, and a
 * numbered pager. Every row opens the permanent record. Entirely client-side;
 * the server owns the real index (§8). View state lives in the URL (§6).
 */
export function ClaimsAllPage() {
  usePageTopBar({ breadcrumb: ['Claims', 'Every claim'] });

  const [state, patch] = useListUrlState({
    q: '',
    range: 'last-90',
    status: '',
    payer: '',
    sort: 'visit',
    page: '1',
  });

  // Iris Calder's claim closes as Paid to the patient once Posting posts it.
  const paidToPatientVersion = usePaidToPatientVersion();

  const preset = state.range as HistoryDatePreset;
  const page = Math.max(1, Number(state.page) || 1);
  const byChange = state.sort === 'change';

  // Rows matching search + status + payer, ignoring the date window.
  const matched = useMemo(() => {
    let rows = applySearch(getHistoryClaims(), state.q, searchText);
    if (state.status) rows = rows.filter((c) => c.status === state.status);
    if (state.payer) rows = rows.filter((c) => c.payer === state.payer);
    return rows;
    // paidToPatientVersion: re-read the claim set when Posting posts a paid-to-the-patient claim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.q, state.status, state.payer, paidToPatientVersion]);

  const filtered = useMemo(() => {
    const dateRange = windowFor(preset);
    const rows = matched.filter((c) => inRange(c.visitIso, dateRange));
    return sortBy(rows, (c) => (byChange ? c.lastChangeIso : c.visitIso), -1);
  }, [matched, preset, byChange]);

  const pageRows = paginate(filtered, page, PAGE_SIZE);
  const needsPerson = filtered.filter((c) => c.needsPerson).length;

  // Search matches outside the current window (§3): "N more in other dates".
  const outside = state.q.trim() !== '' && preset !== 'all-time' ? matched.length - filtered.length : 0;

  const narrowed =
    state.q !== '' || state.payer !== '' || state.status !== '' || preset !== 'all-time';

  const headline = (
    <>
      {filtered.length.toLocaleString()} {filtered.length === 1 ? 'claim' : 'claims'} from {windowLabel(preset)}.
      {needsPerson > 0 ? <> <em>{needsPerson.toLocaleString()} need a person.</em></> : null}
    </>
  );

  const bandRight = (
    <BandButton variant="g" onClick={() => { exportCsv(filtered); }}>Export CSV</BandButton>
  );

  const tabs = (
    <>
      <BandTab label="Needs you" to="/claims" />
      <BandTab label="All claims" to="/claims/all" active />
    </>
  );

  return (
    <div className="clm clm-all">
      <CompactBand headline={headline} right={bandRight} tabs={tabs} />
      <div className="clm-all-body">
        <FilterBar
          search={state.q}
          onSearch={(v) => { patch({ q: v, page: '1' }); }}
          searchDebounceMs={300}
          searchPlaceholder="Patient, claim number, payer claim number or amount"
          dateRange={preset}
          onDateRange={(v) => { patch({ range: v, page: '1' }); }}
        >
          <select
            className="lx-select"
            value={state.status}
            aria-label="Status"
            onChange={(e) => { patch({ status: e.target.value, page: '1' }); }}
          >
            <option value="">Any status</option>
            {CLAIM_STATUSES.map((s) => (
              <option key={s} value={s}>{CLAIM_STATUS_LABEL[s]}</option>
            ))}
          </select>
          <select
            className="lx-select"
            value={state.payer}
            aria-label="Payer"
            onChange={(e) => { patch({ payer: e.target.value, page: '1' }); }}
          >
            <option value="">All payers</option>
            {PAYER_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </FilterBar>

        {outside > 0 ? (
          <p className="clm-all-outside">
            {outside.toLocaleString()} more in other dates ·{' '}
            <button
              type="button"
              className="lx-empty-link"
              onClick={() => { patch({ range: 'all-time', page: '1' }); }}
            >
              Show all time
            </button>
          </p>
        ) : null}

        {filtered.length === 0 ? (
          <div className="clm-card">
            <ListEmpty
              query={state.q || undefined}
              windowLabel={windowLabel(preset)}
              {...(narrowed && preset !== 'all-time'
                ? { onSearchAllTime: () => { patch({ range: 'all-time', page: '1' }); } }
                : {})}
            />
          </div>
        ) : (
          <>
            <div className="clm-card clm-all-tablecard">
              <div className="clm-tbl-wrap">
                <table className="clm-tbl clm-all-table">
                  <thead>
                    <tr>
                      <th>Claim</th>
                      <th>Patient</th>
                      <th>Payer</th>
                      <th>Codes</th>
                      <th className="ta-right">Billed</th>
                      <th className="ta-right">Paid</th>
                      <th>Where it is</th>
                      <th>Last change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((c) => (
                      <tr key={c.id} className="clm-all-row">
                        <td>
                          <Link to={`/claims/record/${c.id}`} className="clm-all-link">
                            {c.id}
                          </Link>
                        </td>
                        <td>
                          <b style={{ fontWeight: 600 }}>{c.patient}</b>
                          <span className="clm-all-visit">visit {c.visitShort}</span>
                        </td>
                        <td>{c.payer}</td>
                        <td className="clm-code clm-all-codes">{c.codes.join(', ')}</td>
                        <td className="ta-right num">{usd(c.billed)}</td>
                        <td className="ta-right num">{c.paid == null ? '—' : usd(c.paid)}</td>
                        <td>
                          <span className="clm-where">
                            <i className={`clm-wdot t-${c.tone}`} aria-hidden />
                            {c.where}
                          </span>
                        </td>
                        <td className="num clm-all-change">{c.lastChangeLabel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="clm-all-pagerrow">
              <NumberedPager
                page={page}
                pageSize={PAGE_SIZE}
                total={filtered.length}
                onPage={(p) => { patch({ page: String(p) }); }}
              />
              <span className="clm-all-sort">
                {byChange ? (
                  <>Last change first · <button type="button" className="lx-empty-link" onClick={() => { patch({ sort: 'visit', page: '1' }); }}>Sort by visit</button></>
                ) : (
                  <>Newest visit first · <button type="button" className="lx-empty-link" onClick={() => { patch({ sort: 'change', page: '1' }); }}>Sort by last change</button></>
                )}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
